/**
 * FileMakerExecuteDataAPI.ts - WebViewer integration for Execute FileMaker Data API script step
 * 
 * CHANGE HISTORY:
 * - 251102_1046 [pending] danielbeer: Initial creation for native Data API integration
 * 
 * Provides WebViewer-side interface for FileMaker's Execute FileMaker Data API script step
 * This is for FileMaker 19+ and uses the native script step instead of HTTP requests
 */

import { getFileMakerBridge } from './FileMakerBridge';

export interface ExecuteDataAPIOptions {
  /** Database name (defaults to current file) */
  database?: string;
  
  /** Layout name (defaults to current layout) */
  layout?: string;
  
  /** WebViewer object name for callbacks */
  webViewerObject?: string;
  
  /** Timeout for operations (ms) */
  timeout?: number;
}

export interface DataAPIGetOptions extends ExecuteDataAPIOptions {
  /** Record ID to retrieve */
  recordId: string;
}

export interface DataAPICreateOptions extends ExecuteDataAPIOptions {
  /** Field data to create */
  fieldData: Record<string, any>;
  
  /** Portal data (optional) */
  portalData?: Record<string, any[]>;
  
  /** Script to run after creation */
  script?: string;
  
  /** Script parameter */
  scriptParam?: string;
}

export interface DataAPIUpdateOptions extends ExecuteDataAPIOptions {
  /** Record ID to update */
  recordId: string;
  
  /** Field data to update */
  fieldData: Record<string, any>;
  
  /** Portal data (optional) */
  portalData?: Record<string, any[]>;
}

export interface DataAPIDeleteOptions extends ExecuteDataAPIOptions {
  /** Record ID to delete */
  recordId: string;
}

export interface DataAPIFindOptions extends ExecuteDataAPIOptions {
  /** Find query (FileMaker Data API format) */
  query: Array<Record<string, any>>;
  
  /** Maximum records to return */
  limit?: number;
  
  /** Offset for pagination */
  offset?: number;
  
  /** Sort order */
  sort?: Array<{ fieldName: string; sortOrder: 'ascend' | 'descend' }>;
  
  /** Script to run before sort */
  scriptPresort?: string;
}

export interface DataAPIResult {
  success: boolean;
  data?: any;
  error?: string;
  messages?: Array<{ code: string; message: string }>;
}

/**
 * FileMaker Execute Data API Integration
 * Uses FileMaker's native Execute FileMaker Data API script step (FM 19+)
 */
export class FileMakerExecuteDataAPI {
  private fm = getFileMakerBridge();
  private callbacks: Map<string, (result: DataAPIResult) => void> = new Map();
  private callbackCounter: number = 0;

  constructor() {
    this.registerCallbackHandler();
  }

  /**
   * Register the global callback handler for Data API results
   */
  private registerCallbackHandler(): void {
    (window as any).fm_dataapi_callback = (callbackId: string, resultJSON: string) => {
      try {
        const result: DataAPIResult = JSON.parse(resultJSON);
        const callback = this.callbacks.get(callbackId);
        
        if (callback) {
          callback(result);
          this.callbacks.delete(callbackId);
        }
      } catch (error) {
        console.error('[FileMakerExecuteDataAPI] Callback error:', error);
      }
    };
  }

  /**
   * Get a record by ID
   */
  async get(options: DataAPIGetOptions): Promise<DataAPIResult> {
    return this.executeOperation('get', {
      database: options.database,
      layout: options.layout,
      recordId: options.recordId,
      webViewerObject: options.webViewerObject,
      timeout: options.timeout
    });
  }

  /**
   * Create a new record
   */
  async create(options: DataAPICreateOptions): Promise<DataAPIResult> {
    return this.executeOperation('create', {
      database: options.database,
      layout: options.layout,
      fieldData: JSON.stringify(options.fieldData),
      portalData: options.portalData ? JSON.stringify(options.portalData) : undefined,
      script: options.script,
      scriptParam: options.scriptParam,
      webViewerObject: options.webViewerObject,
      timeout: options.timeout
    });
  }

  /**
   * Update an existing record
   */
  async update(options: DataAPIUpdateOptions): Promise<DataAPIResult> {
    return this.executeOperation('update', {
      database: options.database,
      layout: options.layout,
      recordId: options.recordId,
      fieldData: JSON.stringify(options.fieldData),
      portalData: options.portalData ? JSON.stringify(options.portalData) : undefined,
      webViewerObject: options.webViewerObject,
      timeout: options.timeout
    });
  }

  /**
   * Delete a record
   */
  async delete(options: DataAPIDeleteOptions): Promise<DataAPIResult> {
    return this.executeOperation('delete', {
      database: options.database,
      layout: options.layout,
      recordId: options.recordId,
      webViewerObject: options.webViewerObject,
      timeout: options.timeout
    });
  }

  /**
   * Find records
   */
  async find(options: DataAPIFindOptions): Promise<DataAPIResult> {
    return this.executeOperation('find', {
      database: options.database,
      layout: options.layout,
      query: JSON.stringify(options.query),
      limit: options.limit?.toString(),
      offset: options.offset?.toString(),
      sort: options.sort ? JSON.stringify(options.sort) : undefined,
      scriptPresort: options.scriptPresort,
      webViewerObject: options.webViewerObject,
      timeout: options.timeout
    });
  }

  /**
   * Execute a Data API operation
   */
  private async executeOperation(
    operation: string,
    params: Record<string, any>
  ): Promise<DataAPIResult> {
    return new Promise((resolve, reject) => {
      const callbackId = `dataapi_${this.callbackCounter++}`;
      const timeout = params.timeout || 30000;

      // Set timeout
      const timeoutId = setTimeout(() => {
        this.callbacks.delete(callbackId);
        reject(new Error('Data API operation timed out'));
      }, timeout);

      // Register callback
      this.callbacks.set(callbackId, (result: DataAPIResult) => {
        clearTimeout(timeoutId);
        resolve(result);
      });

      // Call FileMaker script
      this.fm.callScript('ExecuteDataAPI', {
        operation,
        callbackId,
        callbackFunction: 'fm_dataapi_callback',
        ...params
      });
    });
  }

  /**
   * Build a find query
   * Helper to construct FileMaker Data API find queries
   */
  buildQuery(criteria: Record<string, any>): Array<Record<string, any>> {
    const query: Array<Record<string, any>> = [];
    const queryItem: Record<string, any> = {};

    for (const [field, value] of Object.entries(criteria)) {
      if (typeof value === 'object' && value !== null) {
        // Handle operators
        if ('$eq' in value) {
          queryItem[field] = value.$eq;
        } else if ('$ne' in value) {
          queryItem[field] = `!${value.$ne}`;
        } else if ('$gt' in value) {
          queryItem[field] = `>${value.$gt}`;
        } else if ('$gte' in value) {
          queryItem[field] = `>=${value.$gte}`;
        } else if ('$lt' in value) {
          queryItem[field] = `<${value.$lt}`;
        } else if ('$lte' in value) {
          queryItem[field] = `<=${value.$lte}`;
        } else if ('$contains' in value) {
          queryItem[field] = `*${value.$contains}*`;
        } else if ('$beginsWith' in value) {
          queryItem[field] = `${value.$beginsWith}*`;
        } else if ('$endsWith' in value) {
          queryItem[field] = `*${value.$endsWith}`;
        }
      } else {
        // Direct value
        queryItem[field] = value;
      }
    }

    query.push(queryItem);
    return query;
  }

  /**
   * Clean up all callbacks
   */
  cleanup(): void {
    this.callbacks.clear();
    delete (window as any).fm_dataapi_callback;
  }
}

// Singleton instance
let executeDataAPIInstance: FileMakerExecuteDataAPI | null = null;

export function getFileMakerExecuteDataAPI(): FileMakerExecuteDataAPI {
  if (!executeDataAPIInstance) {
    executeDataAPIInstance = new FileMakerExecuteDataAPI();
  }
  return executeDataAPIInstance;
}
